import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Gauge, FileSignature, Headphones, ArrowRight, ShieldCheck, Zap, Sparkles, Clock, Award, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// The three assistant marketing profiles. Each has a hero, features grid,
// use-cases, and a "try the tool" deep-link back into the portal Hub.
const PROFILES = {
  "website-audit": {
    icon: Gauge,
    kicker: "AI Website Auditor",
    heading: "Turn any URL into an actionable growth plan — in 20 seconds.",
    sub: "Paste a URL. Get an SEO, speed, conversion and trust score, a verdict, top wins ranked by effort × impact, and a fixes-by-area checklist your team can ship this afternoon.",
    color: "from-indigo-600 to-indigo-800",
    accent: "indigo",
    tryPath: "/portal/assistants/website-audit",
    hero_bullets: [
      "Fetches the page live — never scores blind",
      "Powered by Claude Sonnet 4.6 — no guessing, no fluff",
      "Every fix has a `why` and an `effort` tag",
    ],
    features: [
      { icon: Zap, title: "Deterministic signals", body: "We read title/meta/H1/H2/CTA/canonical/viewport/img-alt/form/script counts *before* the LLM sees the page — so scores are grounded in real evidence." },
      { icon: Sparkles, title: "4 scoring bands", body: "SEO · Speed · Conversion · Trust — each with a 0-100 score and specific fixes, so you know where to spend the next hour." },
      { icon: Clock, title: "Uplift estimate", body: "Every audit ends with a plain-English uplift band (e.g. \"+15-25% organic clicks in 60 days\") — so you can prioritize." },
      { icon: ShieldCheck, title: "Auditable", body: "Every audit is saved and can be replayed by an admin — no black-box, no lost work." },
    ],
    usecases: [
      "Web agency running discovery — audit a prospect's site inside the sales call",
      "Marketing manager evaluating an inherited site before spending on ads",
      "Freelancer producing a paid audit deliverable in minutes instead of hours",
    ],
  },
  "proposal-writer": {
    icon: FileSignature,
    kicker: "AI Proposal Writer",
    heading: "Describe the job — get a client-ready proposal live-streamed to your screen.",
    sub: "Type the brief, pick a tone, and watch a full proposal (executive summary, scope, timeline, investment, assumptions, next steps) appear token-by-token. Copy the markdown, edit it, send it.",
    color: "from-emerald-600 to-emerald-800",
    accent: "emerald",
    tryPath: "/portal/assistants/proposal-writer",
    hero_bullets: [
      "Streams live — first words in under 2 seconds",
      "3 lengths · 3 tones · any currency",
      "Never invents credentials — only uses what's in the brief",
    ],
    features: [
      { icon: Sparkles, title: "Streaming output", body: "Powered by GPT-5.4 with SSE streaming — you see the proposal being written, not a spinner." },
      { icon: Zap, title: "Auto quote hint", body: "We extract the largest currency figure from the draft and surface it as a headline quote hint — so you can price at a glance." },
      { icon: FileSignature, title: "Copy-ready markdown", body: "Every proposal renders in-app and copies as clean markdown that pastes into Notion, Docs, or your CRM." },
      { icon: ShieldCheck, title: "Honest by design", body: "Never invents awards or certifications. If the brief doesn't mention it, the proposal doesn't claim it." },
    ],
    usecases: [
      "Service business responding to an inbound RFP in minutes",
      "Freelancer converting a discovery call into a sendable SOW same-day",
      "Sales team drafting proposals that stay on-brand across every rep",
    ],
  },
  "sales-coach": {
    icon: Headphones,
    kicker: "AI Sales Coach",
    heading: "Paste any call transcript — get a scorecard, wins, misses and a next-call plan.",
    sub: "The coach reads the whole call, scores 5 dimensions, calls out what worked and what didn't, and hands your rep a specific opening line and 3 questions for the very next conversation.",
    color: "from-amber-600 to-amber-800",
    accent: "amber",
    tryPath: "/portal/assistants/sales-coach",
    hero_bullets: [
      "5-dimension scorecard with per-dimension notes",
      "Grade A-F · honest, kind, actionable",
      "A prep checklist for the very next call",
    ],
    features: [
      { icon: Award, title: "5-dimension scorecard", body: "Discovery · Rapport · Value framing · Objection handling · Next-step clarity. Each with a 0-100 score and a one-sentence explanation." },
      { icon: Sparkles, title: "Wins + misses + risks", body: "Powered by Gemini 2.5 Flash — surfaces what the rep did well, what to fix, and any compliance/risk flags to escalate." },
      { icon: Zap, title: "Next-call plan", body: "A ready-to-use opening line, three qualifying questions, and a prep checklist — so every rep walks into the next call prepared." },
      { icon: ShieldCheck, title: "Never invents", body: "If the transcript is too thin, the coach marks a dimension as 'insufficient' rather than guessing — so your team trusts the score." },
    ],
    usecases: [
      "SDR manager reviewing 20 calls a day without burning out on note-taking",
      "Owner-operator upskilling a junior rep with per-call feedback",
      "Team lead building a shared playbook of what actually works",
    ],
  },
};

export default function AssistantMarketingPage() {
  const { key } = useParams();
  const profile = PROFILES[key];
  useEffect(() => {
    if (profile) document.title = `${profile.kicker} — WebsiteTech.pro`;
  }, [profile]);
  if (!profile) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="text-2xl font-bold text-slate-900">Assistant not found</div>
        <Link to="/ai-assistants" className="mt-4 inline-flex items-center gap-1 text-indigo-700 hover:underline">
          Browse all assistants
        </Link>
      </div>
      <Footer />
    </div>
  );

  const Icon = profile.icon;
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className={`relative overflow-hidden text-white bg-gradient-to-br ${profile.color}`} data-testid={`assistant-marketing-${key}`}>
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 30% 30%, white 0%, transparent 50%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold border border-white/30 bg-white/10 rounded-full px-3 py-1">
              <Sparkles className="w-3.5 h-3.5" /> {profile.kicker}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl">
              {profile.heading}
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-2xl">{profile.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={profile.tryPath} data-testid={`marketing-try-${key}`}
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-white/90 px-6 py-3.5 rounded font-bold transition shadow-lg">
                Try it now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/ai-assistants"
                className="inline-flex items-center gap-2 border border-white/40 hover:border-white bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded font-semibold transition">
                See all AI Assistants
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
              {profile.hero_bullets.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-white/90" data-testid={`marketing-bullet-${key}-${i}`}>
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-white" /> {b}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 flex justify-center">
            <div className="rounded-3xl bg-white/10 backdrop-blur border border-white/20 p-10">
              <Icon className="w-40 h-40 text-white/90" strokeWidth={1.2} />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-indigo-700 font-bold">How it works</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900 max-w-3xl">
            Four capabilities that make the difference.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile.features.map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={i} data-testid={`marketing-feature-${key}-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 p-6 transition">
                  <div className="w-11 h-11 rounded-lg bg-indigo-50 border border-indigo-200 grid place-items-center">
                    <FIcon className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div className="mt-4 text-lg font-bold text-slate-900">{f.title}</div>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-indigo-700 font-bold">Who this is for</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900 max-w-3xl">Built for the people doing the work.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {profile.usecases.map((u, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-black grid place-items-center">{i + 1}</div>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{u}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-20 text-white bg-gradient-to-br ${profile.color}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold">Try it — no signup required.</h2>
          <p className="mt-3 text-white/90 text-lg">Every result is auditable and can be routed straight into your CRM as a lead.</p>
          <div className="mt-8">
            <Link to={profile.tryPath}
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-white/90 px-8 py-4 rounded font-bold transition shadow-lg">
              Try {profile.kicker} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


// The public /ai-assistants landing that lists all 3.
export function AssistantsLandingPage() {
  useEffect(() => { document.title = "AI Assistants — WebsiteTech.pro"; }, []);
  const cards = [
    { key: "website-audit", ...PROFILES["website-audit"] },
    { key: "proposal-writer", ...PROFILES["proposal-writer"] },
    { key: "sales-coach", ...PROFILES["sales-coach"] },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 0%, transparent 40%), radial-gradient(circle at 80% 70%, white 0%, transparent 40%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-24 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-indigo-200 font-bold border border-white/20 bg-white/10 rounded-full px-3 py-1">
            <Sparkles className="w-3.5 h-3.5" /> AI Assistants Hub
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold max-w-4xl mx-auto leading-[1.05] tracking-tight">
            Three AI Assistants. Every small-business job, one minute or less.
          </h1>
          <p className="mt-6 text-lg text-indigo-100 max-w-2xl mx-auto">
            Purpose-built AI that audits your website, drafts a proposal, or coaches your next sales call.
            No signup. No black boxes. Every run is auditable.
          </p>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.key} to={`/ai-assistants/${c.key}`}
                data-testid={`landing-card-${c.key}`}
                className={`group rounded-2xl p-8 text-white bg-gradient-to-br ${c.color} hover:shadow-2xl hover:-translate-y-1 transition-all`}>
                <div className="w-14 h-14 rounded-xl bg-white/20 grid place-items-center">
                  <Icon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <div className="mt-5 text-2xl font-bold">{c.kicker}</div>
                <p className="mt-3 text-white/90 text-sm leading-relaxed">{c.heading}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}
